import "./AccountDetail.css";
import { storeUser } from "../../../../redux/users/usersSlice";
import { toastify } from "../../../../helpers/toast/toastify";
import { useDispatch, useSelector } from "react-redux";
import editIcon from "../../../../assets/images/edit.svg";
import FloatingTextFiled from "../../CommonComponents/FloatingTextFiled/FloatingTextFiled";
import React, { useEffect, useRef, useState } from "react";
import useApi from "../../../../helpers/apiHelper/requestHelper";
import { fetchUser } from "../../../../helpers/helper";
import BubbleSvgButton from "../../CommonComponents/BubbleSvgButton/BubbleSvgButton";
import VerificationApiModal from "./VerificationModal";

const AccountDetail = () => {
  const api = useApi();
  const dispatch = useDispatch();
  const user = useSelector((state) => state?.user?.user);
  const [email, setEmail] = useState(null);
  const [originalEmail, setOriginalEmail] = useState(null);
  const [alredayEmail, setAlredayEmail] = useState(null);
  const [editableField, setEditableField] = useState(null);
  const [showButton, setShowButton] = useState(false);
  const [again, setAgain] = useState(false);
  
  // OTP Verification States
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [newEmail, setNewEmail] = useState(null);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpVerificationId, setOtpVerificationId] = useState(null);
  
  const inputRefs = useRef({});

  // Handle click outside functionality for email
  useEffect(() => {
    const handleClickOutside = (event) => {
      console.log("Click detected", { editableField, target: event.target });
      
      if (editableField === "email") {
        const editableFieldContainer = event.target.closest('.editable-field');
        const saveButtonContainer = event.target.closest('.d-flex');
        
        if (!editableFieldContainer && !saveButtonContainer) {
          console.log("Clicking outside - resetting email field");
          setEmail(originalEmail);
          setEditableField(null);
          setShowButton(false);
        }
      }
    };

    if (editableField === "email") {
      document.addEventListener('click', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [editableField, originalEmail]);

  const handleEditClick = (fieldId) => {
    setEditableField(fieldId);

    setTimeout(() => {
      if (inputRefs.current[fieldId]) inputRefs.current[fieldId].focus();
    }, 100);
  };

  const sendOtpToCurrentEmail = async () => {
    try {
      setIsVerifyingOtp(true);
      const response = await api("post", "users/updateAccountOTP");
      
      if (response?.status) {
        setOtpVerificationId(response?.data?.verificationId || Date.now().toString());
        setIsVerifyingOtp(false);
        return true;
      } else {
        toastify("Failed to send OTP. Please try again.", "error");
        setIsVerifyingOtp(false);
        return false;
      }
    } catch (error) {
      console.error("Error sending OTP:", error);
      toastify("Error sending OTP. Please try again.", "error");
      setIsVerifyingOtp(false);
      return false;
    }
  };

  const verifyOtp = async (otp) => {
    try {
      const response = await api("post", "users/verifyAccountOTP", {
        otp,
      });

      if (response?.data?.verified) {
        setOtpVerified(true);
        return true;
      } else {
        toastify("Invalid OTP. Please try again.", "error");
        return false;
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      toastify("Error verifying OTP. Please try again.", "error");
      return false;
    }
  };

  const updateEmailAfterOtpVerification = async () => {
    try {
      if (!/\S+@\S+\.\S+/.test(newEmail)) {
        toastify("Please enter a valid email address", "error");
        return false;
      }

      const response = await api("post", "multiCommunity/updateUserEmail", {
        email: newEmail,
        mainLeader: user?.mainLeader?._id,
      });

      console.log(response, "this is a response of the email edit");
      
      if (response?.status) {
        setEditableField(null);
        setShowButton(false);
        setOriginalEmail(newEmail);
        setEmail(newEmail);
        setAlredayEmail(newEmail);
        await fetchUser(api, dispatch, storeUser);
        toastify("Your email has been changed successfully.", "success");
        return true;
      } else {
        toastify(response.message.includes("duplicate key error collection")?"This Email already exist please use another email" :"Failed to update email. Please try again.", "error");
        return false;
      }
    } catch (error) {
      console.error("Error updating email:", error);
      toastify(error.message.includes("duplicate key error collection")?"This Email already exist please use another email":"Error updating email. Please try again.", "error");
      return false;
    }
  };

  const save = async () => {
  // Reset OTP states
  setOtpVerified(false);
  setOtpVerificationId(null);
  
  if (alredayEmail !== email) {
    if (!/\S+@\S+\.\S+/.test(email)) {
      toastify("Please enter a valid email address", "error");
      return;
    }

    // Store the new email for later use
    setNewEmail(email);
    
    // Show OTP verification modal (using existing modal)
    setShowOtpModal(true);

  } else {
    setEditableField(null);
    setShowButton(false);
    toastify("You did not change email", "error");
  }
};

// Add this function to handle OTP verification success
const handleOtpVerified = async () => {
  // OTP verified successfully, now update the email
  const updated = await updateEmailAfterOtpVerification();
  
  if (updated) {
    setShowOtpModal(false);
  }
};

  const handleOtpVerificationComplete = async (otp) => {
    const verified = await verifyOtp(otp);
    
    if (verified) {
      // OTP verified successfully, now update the email
      const updated = await updateEmailAfterOtpVerification();
      
      if (updated) {
        setShowOtpModal(false);
        setOtpVerified(false);
        setOtpVerificationId(null);
      }
    }
  };

  const handleResendOtp = async () => {
    await sendOtpToCurrentEmail();
  };

  const handleCloseOtpModal = () => {
    setShowOtpModal(false);
    setOtpVerified(false);
    setOtpVerificationId(null);
    setIsVerifyingOtp(false);
    // Reset email to original if modal is closed without verification
    setEmail(originalEmail);
    setEditableField(null);
    setShowButton(false);
  };

  useEffect(() => {
    if (user?.role === "leader") {
      setEmail(user?.mainLeader?.email);
      setOriginalEmail(user?.mainLeader?.email);
      setAlredayEmail(user?.mainLeader?.email);
    } else {
      setEmail(user?.email);
      setOriginalEmail(user?.email);
      setAlredayEmail(user?.email);
    }
  }, [user, again]);

  return (
    <>
      <div className="accountDetailBox">
        <div className="headingBox">
          <h2 className="title">Account Details</h2>{" "}
        </div>
        <div
          className={`account-detail-fields-row d-flex ${
            showButton ? "gap-3" : ""
          }`}
        >
          <div className="editable-field w-50 securityField editableSettingsFields">
            <FloatingTextFiled
              className={`${
                editableField !== "email" ? "accdetailinputbox" : "accdetailinputbox-active"
              }`}
              disabled={editableField !== "email"}
              forLabel="email"
              id="email"
              label="Email"
              onChange={(e) => {
                setShowButton(true);
                setEmail(e.target.value);
              }}
              placeholder="Email"
              ref={(el) => (inputRefs.current["email"] = el)}
              type="email"
              value={email}
              focusedAllowed={false}
            />
            <span
              className="edit-icon"
              onClick={() => handleEditClick("email")}
              style={{ cursor: "pointer", marginLeft: "10px", opacity: 1 }}
            >
              <img alt="Edit Icon" src={editIcon} />
            </span>
          </div>

          {showButton && (
            <div className="editable-field fixedWidth1440-298 w-50 editableSettingsFieldssecurityInputsRow d-flex justify-content-end align-items-center">
              <BubbleSvgButton
                className="accountSecurityBtn"
                onClick={save}
                text={"Save edits"}
                type="button"
                variant="green"
                containerStyling={{
                  justifyContent: "center",
                  padding: 0,
                }}
                disabled={isVerifyingOtp}
                isLoading={isVerifyingOtp}
              />
            </div>
          )}
        </div>
 
        {/* Responsive column on mobile */}
        <style>{`
          @media (max-width: 768px) {
            .account-detail-fields-row {
              flex-direction: column !important;
              gap: 20px !important;
            }
            .account-detail-fields-row .w-50 {
              width: 100% !important;
            }
            .account-detail-fields-row .fixedWidth1440-298 {
              width: 100% !important;
              margin: 0;
            }
          }
        `}</style>
 
      </div>

      {/* Or use your existing VerificationApiModal component if you prefer */}
      <VerificationApiModal
        showEmailPopUp={{ show: showOtpModal }}
        setShowEmailPopUp={(value) => setShowOtpModal(value.show)}
        onClose={handleCloseOtpModal}
        callApi={updateEmailAfterOtpVerification}
      />
    </>
  );
};

export default AccountDetail;